import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPage.css';

const sections = [
    {
        id: 1,
        title: '1. Acceptance of Terms',
        content: `By accessing, downloading or using the Bunchy mobile application (“Bunchy”, “the App”, “the Service”), you agree to be bound by these Terms of Service (“Terms”).

If you do not agree with these Terms, you must not access or use the application.`
    },
    {
        id: 2,
        title: '2. Service Description',
        content: 'Bunchy is a mobile application that provides access to private group spaces created around shared interests, activities, experiences or life situations.',
        negatives: [
            'is not a dating service',
            'does not provide public user profiles',
            'does not offer private one-to-one messaging',
            'does not operate a public feed or algorithmic ranking'
        ],
        note: 'All interactions occur exclusively within private groups.'
    },
    {
        id: 3,
        title: '3. Eligibility',
        content: 'To use Bunchy, you must:',
        bullets: [
            'be at least 18 years old',
            'have the legal capacity to enter into a binding agreement',
            'comply with these Terms and all applicable laws'
        ],
        note: 'By using the Service, you represent and warrant that you meet these requirements.'
    },
    {
        id: 4,
        title: '4. User Accounts',
        content: 'Users are responsible for:',
        bullets: [
            'the accuracy of the information provided',
            'maintaining the confidentiality of their login credentials',
            'all activities that occur under their account'
        ],
        note: 'Bunchy is not responsible for unauthorized access resulting from user negligence.'
    },
    {
        id: 5,
        title: '5. User Conduct',
        content: 'Users agree to use Bunchy in a respectful, lawful and responsible manner.\n\nIn particular, users agree not to:',
        bullets: [
            'post illegal, hateful, threatening, defamatory, obscene or harmful content',
            'harass, intimidate or abuse other users',
            'impersonate another person or entity',
            'share false or misleading information deliberately',
            'misuse the platform or attempt to bypass its security or safeguards'
        ],
        note: 'Violation of these rules may result in temporary suspension or permanent termination of the account, without prior notice.'
    },
    {
        id: 6,
        title: '6. Health-Related, comportments and addictions Groups',
        content: 'Some groups may relate to health, illness, behavior, addiction or personal life situations.',
        note: 'These groups are peer discussion spaces only and are not intended to provide professional support.',
        negatives: [
            'no diagnosis',
            'no treatment',
            'no medical, psychological or professional recommendations'
        ],
        subsections: [
            {
                title: 'Essential rules — Sensitive groups',
                bullets: [
                    'Speak from your own experience - Share what you live. Avoid telling others what to do.',
                    'No judgment, no pressure - No blame, no injunctions, no comparisons.',
                    'No medical or therapeutic advice - No diagnoses, treatments, dosages, or “miracle” solutions.',
                    'No glorification of risky behaviors - Inciting or graphic content is not allowed.',
                    'Respect everyone’s pace and boundaries - Silence, hesitation, or stepping back are always valid.',
                    'Reporting means protecting - If something feels wrong, use the report function.'
                ],
                note: 'These groups are spaces for peer support and sharing. Bunchy is not a medical or therapeutic service.'
            }
        ]
    },
    {
        id: 7,
        title: '7. Moderation & Reporting',
        content: 'Bunchy provides in-app tools to report content, users or behavior.',
        note: 'Bunchy implements reasonable moderation measures but does not actively monitor all content. Moderation decisions are made at Bunchy’s sole discretion.'
    },
    {
        id: 8,
        title: '8. External Interactions',
        content: 'Bunchy is not responsible for meetings, interactions, communications, or activities that may occur outside the application, even if initiated through Bunchy.',
        note: 'Users remain solely responsible for their actions, decisions and personal safety.'
    },
    {
        id: 9,
        title: '9. Suspension & Termination',
        content: 'Users may delete their account at any time via the application.',
        note: 'Bunchy reserves the right to suspend, restrict or permanently terminate any account that violates these Terms or applicable laws.'
    },
    {
        id: 10,
        title: '10. Intellectual Property',
        content: 'All elements of the application including name, logo, design, text, and software are the exclusive property of Bunchy.',
        note: 'User-generated content remains the property of its author, but users grant Bunchy a limited license to display such content within the Service.'
    },
    {
        id: 11,
        title: '11. Limitation of Liability',
        content: 'Content shared on Bunchy is provided exclusively by users.',
        negatives: [
            'does not provide medical, psychological, therapeutic, or professional advice',
            'does not perform diagnoses',
            'does not recommend treatments, practices, or behaviors',
            'does not replace qualified healthcare professionals'
        ],
        note: 'Bunchy cannot be held liable for direct or indirect consequences resulting from following shared content.'
    },
    {
        id: 12,
        title: '12. Availability of the Service',
        content: 'Bunchy does not guarantee uninterrupted or error-free access to the Service.',
        note: 'The Service may be modified, suspended or discontinued at any time without prior notice.'
    },
    {
        id: 13,
        title: '13. Governing Law & Jurisdiction',
        content: 'These Terms are governed by Belgian law, in compliance with applicable European regulations.',
        note: 'Any dispute shall fall under the exclusive jurisdiction of the competent courts of Belgium.'
    },
    {
        id: 14,
        title: '14. Changes to the Terms',
        content: 'These Terms may be updated from time to time.\n\nUsers will be informed of material changes through the application or by appropriate means.'
    }
];

const TermsConditions = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState(null);

    return (
        <div className="legal-page">
            {/* Header */}
            <header className="legal-header">
                <div className="legal-header-inner">
                    <div className="legal-logo-wrap" onClick={() => navigate('/login')}>
                        <img src="/bunchy-logo.svg" alt="Bunchy" className="legal-logo" />
                        <span className="legal-brand">bunchy</span>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <div className="legal-hero">
                <h1 className="legal-hero-title">Terms & Conditions</h1>
            </div>

            {/* Content */}
            <div className="legal-body">
                {/* Sections */}
                <div className="legal-sections">
                    {sections.map(s => (
                        <section
                            key={s.id}
                            id={`section-${s.id}`}
                            className="legal-section"
                        >
                            <h2>{s.title}</h2>

                            {s.content && <p className="legal-para" style={{ whiteSpace: 'pre-wrap' }}>{s.content}</p>}

                            {s.bullets && (
                                <ul className="legal-bullets">
                                    {s.bullets.map((b, i) => <li key={i}>✅ {b}</li>)}
                                </ul>
                            )}

                            {s.negatives && (
                                <div className="legal-negatives">
                                    <span className="bunchy-label">Bunchy:</span>
                                    <ul>
                                        {s.negatives.map((n, i) => <li key={i}>❌ {n}</li>)}
                                    </ul>
                                </div>
                            )}

                            {s.note && <p className="legal-note">ℹ️ {s.note}</p>}

                            {/* Subsections */}
                            {s.subsections && s.subsections.map((sub, si) => (
                                <div key={si} className="legal-subsection">
                                    <h3>{sub.title}</h3>
                                    {sub.content && <p className="legal-para">{sub.content}</p>}
                                    {sub.bullets && (
                                        <ul className="legal-bullets">
                                            {sub.bullets.map((b, i) => <li key={i}>✅ {b}</li>)}
                                        </ul>
                                    )}
                                    {sub.note && <p className="legal-note">ℹ️ {sub.note}</p>}
                                </div>
                            ))}
                        </section>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <footer className="legal-footer">
                <p>Questions? Contact us at <a href="mailto:support@bunchy.io">support@bunchy.io</a></p>
            </footer>
        </div>
    );
};

export default TermsConditions;
